import React, { useEffect, useState } from 'react';
import BaseModal from '../BaseModal';
import identity from 'lodash/identity';
import Button from '../../controls/Button';
import BusyCover from '../../controls/BusyCover/BusyCover';
import { apiGET } from '../../../utils/networking';
import { TextParagraph } from '../../controls/Typography/TextBody';

import './PreviewModal.less'

/**
 *
 * @param {Omit<Parameters<BaseModal>[number], 'controls'> & {
 * documentURL?:string|null;
 * evaluationId?:string|number|null;
 * onClose?(r:boolean):void;
 * buttonContinue?:string;
 * buttonCancel?:string;
 * }} props
 * @returns {React.ReactElement}
 */
export const PreviewModal = (props) => {
  const { onClose, buttonContinue, buttonCancel, modalClass, evaluationId, body, documentURL, ...rest } = props;

  const [docURL, setDocumentURL] = useState(documentURL ?? null);
  const [loading, setLoading] = useState(false);

  const handlePrimaryClick = () => {
    onClose?.(true);
  }

  const handleSecondaryClick = () => {
    onClose?.(false);
  }

  const renderPreviewErrorMessage = () => {
    return <TextParagraph>
      <i className="far fa-exclamation-triangle color-dark-orange mr-2" />
      Unable to preview PDF. Please try again later.
      <br />
      You may still submit and complete by clicking the Submit button below.
    </TextParagraph>;
  }

  /**
   *
   * @param {string} docUrl
   */
  const renderPreviewIframe = (docUrl) => {
    return <iframe
      title="PDF in an i-Frame"
      src={window.location.origin + docUrl}
      frameBorder="1"
      scrolling="auto"
      width="850"
    ></iframe>;
  }

  const getModalBody = () => {
    // if it's loading, don't show anything except the busy cover.
    // if the docURL is null, show nothing.
    // if the docURL is not an empty string, show the iframe with the docURL, otherwise, show the error message.
    return <>
      {docURL === null ? null : docURL ? renderPreviewIframe(docURL) : renderPreviewErrorMessage()}
      <p hidden={!body || loading}>{body}</p>
      <BusyCover isBusy={loading} />
    </>;
  }

  const getControls = () => {
    return <>
      <Button
        hidden={!buttonCancel}
        disabled={loading}
        danger
        size='large'
        onClick={handleSecondaryClick}
      >
        {buttonCancel}
      </Button>
      <Button
        hidden={!buttonContinue}
        disabled={loading}
        primary
        size='large'
        onClick={handlePrimaryClick}
      >
        {buttonContinue}
      </Button>
    </>;
  }

  /**
   *
   * @param {null|string=} documentURL
   * @param {null|number|string=} evaluationId
   */
  const fetchPDF = async (documentURL, evaluationId) => {
    // if it's already loading, don't do anything
    if (loading) { return; }
    setLoading(true);
    try {
      // if documentURL is provided, use it, otherwise, fetch it from the server to ensure it's valid
      const isDocUrlValud = documentURL ?? (evaluationId ? await apiGET(`/recommendation/rest/viewRecommendation/${evaluationId}`) : null);
      // if documentURL is provided, use it, otherwise, we create the documentURL from the evaluationId
      setDocumentURL(isDocUrlValud ? documentURL ?? `/recommendation/rest/viewRecommendation/${evaluationId}` : null);
    } catch (error) {
      console.error(error);
      // set an empty string to indicate that we tried to fetch the documentURL but failed
      setDocumentURL('');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    rest.show ? fetchPDF(documentURL, evaluationId) : void 0;
    return () => {
      setDocumentURL(null);
    }
  }, [evaluationId, documentURL, rest.show]);

  return <BaseModal
    body={getModalBody()}
    controls={getControls()}
    modalClass={['lsn-preview-modal', docURL ? 'has-doc' : 'no-doc', loading ? 'is-busy' : '', modalClass].filter(identity).join(' ')}
    {...rest}
  />;
}

export default PreviewModal;
